import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Chip,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import { getRoleLabel } from '../../utils/userUtils';
import {
  Menu as MenuIcon,
  Dashboard,
  Assignment,
  Schedule,
  Settings,
  People,
  AccountCircle,
  Logout,
  Notifications,
  CalendarMonth,
  Business,
  PictureAsPdf,
  Feedback as FeedbackIcon,
  Route as RouteIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { TenantSettings } from '../../types';
import FeedbackDialog from '../FeedbackDialog/FeedbackDialog';
import ChangelogModal from '../../changelog/ChangelogModal';
import { Footer } from '../Footer/Footer';
import { useAuthStore } from '../../store/authStore';
import { useChangelog } from '../../hooks/useChangelog';
import { tenantKeys } from '../../lib/queryKeys';
import { tenantService } from '../../services/tenantService';

const drawerWidth = 240;

interface NavigationItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  roles?: string[];
  /** Feature-Flag: Nur anzeigen wenn true */
  featureFlag?: string;
}

const navigationItems: NavigationItem[] = [
  {
    text: 'Dashboard',
    icon: <Dashboard />,
    path: '/dashboard',
  },
  {
    text: 'Aufträge',
    icon: <Assignment />,
    path: '/orders',
  },
  {
    text: 'PDF Verarbeitung',
    icon: <PictureAsPdf />,
    path: '/pdf-processing',
    roles: ['ADMIN', 'AGENT', 'GLOBAL_ADMIN'],
  },
  {
    text: 'Verfügbarkeit',
    icon: <Schedule />,
    path: '/availability',
  },
  {
    text: 'Kalender',
    icon: <CalendarMonth />,
    path: '/calendar',
  },
  {
    text: 'Tagesroute',
    icon: <RouteIcon />,
    path: '/route',
    featureFlag: 'enable_subcontractors',
  },
  {
    text: 'Benutzer',
    icon: <People />,
    path: '/users',
    roles: ['ADMIN', 'GLOBAL_ADMIN'],
  },
  {
    text: 'Benachrichtigungen',
    icon: <Notifications />,
    path: '/notifications',
    roles: ['ADMIN', 'GLOBAL_ADMIN'],
  },
  {
    text: 'Einstellungen',
    icon: <Settings />,
    path: '/settings',
    roles: ['ADMIN', 'GLOBAL_ADMIN'],
  },
  {
    text: 'Mandanten',
    icon: <Business />,
    path: '/tenants',
    roles: ['GLOBAL_ADMIN'],
  },
];

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, tenant, logout } = useAuthStore();
  const { showChangelog, dismissChangelog } = useChangelog();

  // Tenant-Settings für Feature-Flags im Menü laden
  const { data: settingsData } = useQuery({
    queryKey: tenantKeys.settings(tenant?.id),
    queryFn: () => tenantService.getTenantSettings(tenant!.id),
    enabled: !!tenant?.id,
    staleTime: 5 * 60 * 1000,
  });
  const tenantSettings = settingsData?.settings;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout(); // SEC-H01: async — wartet auf serverseitiges Token-Revoke
    navigate('/login', { replace: true }); // replace: true → Browser-Back führt nicht zurück in geschützte Bereiche
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  // Filter navigation items based on user role and feature flags
  const filteredNavigationItems = navigationItems.filter((item) => {
    if (item.roles && (!user || !item.roles.includes(user.role))) return false;
    if (item.featureFlag) {
      if (!tenantSettings) return false;
      const key = item.featureFlag as keyof TenantSettings;
      return tenantSettings[key] === true;
    }
    return true;
  });

  const drawer = (
    <div>
      <Toolbar>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #942642 0%, #b53050 100%)',
              flexShrink: 0,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="9" stroke="#FFCC00" strokeWidth="2" fill="none" />
              <line x1="12" y1="12" x2="12" y2="6" stroke="#FFCC00" strokeWidth="2" strokeLinecap="round" />
              <line x1="12" y1="12" x2="16" y2="12" stroke="#FFCC00" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </Box>
          <Typography variant="h6" noWrap component="div" fontWeight="bold">
            WeltiTimely
          </Typography>
        </Box>
      </Toolbar>
      <Divider />

      {/* Tenant Info */}
      {tenant && (
        <Box sx={{ p: 2 }}>
          <Chip label={`${tenant.name} (${tenant.key})`} color="primary" size="small" sx={{ width: '100%' }} />
        </Box>
      )}

      <List>
        {filteredNavigationItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={
                item.path === '/dashboard' ? location.pathname === item.path : location.pathname.startsWith(item.path)
              }
              onClick={() => handleNavigation(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Skip-to-Content Link für Barrierefreiheit */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          zIndex: 9999,
          '&:focus': {
            position: 'fixed',
            top: 8,
            left: 8,
            width: 'auto',
            height: 'auto',
            overflow: 'visible',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            px: 2,
            py: 1,
            borderRadius: 1,
            fontSize: '0.875rem',
            fontWeight: 600,
            boxShadow: 4,
          },
        }}
      >
        Zum Inhalt springen
      </Box>

      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="Navigation öffnen"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {/* Page title based on current route (auch Sub-Seiten) */}
            {navigationItems.find((item) =>
              item.path === '/dashboard' ? location.pathname === item.path : location.pathname.startsWith(item.path),
            )?.text || 'WeltiTimely'}
          </Typography>

          {/* User Menu */}
          <Button color="inherit" onClick={handleProfileMenuOpen} startIcon={<AccountCircle />}>
            {user?.full_name || user?.email}
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem
              onClick={() => {
                handleProfileMenuClose();
                navigate('/profile');
              }}
            >
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2">{user?.email}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {getRoleLabel(user?.role || '')} • Profil bearbeiten
                </Typography>
              </ListItemText>
            </MenuItem>

            <Divider />

            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Abmelden</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        id="main-content"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        <Toolbar /> {/* Responsive spacer for AppBar height */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          <Outlet />
          {/* Footer */}
          <Footer />
        </Box>
      </Box>

      {/* Feedback FAB */}
      <Fab
        color="primary"
        aria-label="Feedback geben"
        onClick={() => setFeedbackOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1200,
        }}
      >
        <FeedbackIcon />
      </Fab>

      {/* Feedback Dialog */}
      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      {/* Changelog Modal */}
      <ChangelogModal open={showChangelog} onClose={dismissChangelog} />
    </Box>
  );
};

export default Layout;
